import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import StorefrontIcon from "@mui/icons-material/Storefront";
import OceshaLogo from "../../assets/ocesha_images/ocesha_logo.png";
import {
  exchangeSessionCode,
  isInIframe,
  markEmbedded,
  rememberAppKey,
  startEmbeddedSession,
  startShoplineOauth,
} from "../../utils/shoplineEmbedded";

/**
 * Entry point inside the SHOPLINE admin iframe.
 * The backend App URL (/api/integrations/shopline/install) sends the iframe here with:
 *   ?authorize=1&app_key&scope&redirect_uri&auth_url   → store not authorized yet: run OAuth
 *   ?code=…                                             → one-time code: start the session
 *   ?error=…&reason=…                                   → show what went wrong
 *   ?ended=1                                            → session ended (logout / expired)
 */
type Phase = "loading" | "authorizing" | "authorize_click" | "nested" | "outside" | "ended" | "error";

/**
 * SHOPLINE sometimes finishes OAuth inside the app iframe, which loads a second copy of
 * the admin (menu shown twice). In that case our iframe's parent is not the top window.
 */
const isNestedInAdmin = (): boolean => {
  try { return window.parent !== window.top; } catch { return true; }
};
const NESTED_KEY = "ocesha_nested_breakout";

/** Navigate the real (top) SHOPLINE admin to the app page. Needs a click in most browsers. */
const breakOut = (adminUrl: string): boolean => {
  try { (window.top as Window).location.href = adminUrl; return true; } catch { return false; }
};

const ShoplineEmbeddedApp: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("loading");
  const [message, setMessage] = useState("");

  const handle = params.get("handle") || "";
  const authUrl = params.get("auth_url") || "";
  const adminUrl = params.get("admin_url") || (handle ? `https://${handle}.myshopline.com/admin/apps` : "");

  useEffect(() => {
    if (!isInIframe()) {
      setPhase("outside");
      return;
    }
    const referrerHost = (() => {
      try { return document.referrer ? new URL(document.referrer).host : ""; } catch { return ""; }
    })();
    const host = referrerHost.endsWith("myshopline.com") ? referrerHost : handle ? `${handle}.myshopline.com` : "";
    if (handle) markEmbedded(handle, host);
    if (params.get("app_key")) rememberAppKey(params.get("app_key") || "");

    // Admin loaded inside our iframe → send the real admin to the app page instead.
    // Tried once per minute only, so a wrong guess can never cause a loop.
    if (isNestedInAdmin() && adminUrl) {
      let last = 0;
      try { last = Number(sessionStorage.getItem(NESTED_KEY) || 0); } catch { /* ignore */ }
      if (Date.now() - last > 60000) {
        try { sessionStorage.setItem(NESTED_KEY, String(Date.now())); } catch { /* ignore */ }
        breakOut(adminUrl);
        setPhase("nested");   // shown if the browser blocked the automatic navigation
        return;
      }
    }

    if (params.get("ended")) {
      setPhase("ended");
      return;
    }
    if (params.get("error") || params.get("shopline") === "error") {
      setMessage(params.get("reason") || "Something went wrong while setting up your account.");
      setPhase("error");
      return;
    }
    if (params.get("authorize")) {
      // Loop guard: if authorization was already started twice in the last 2 minutes and we
      // are back here again, stop and let the merchant continue manually.
      const KEY = "ocesha_oauth_attempts";
      const now = Date.now();
      let attempts: number[] = [];
      try { attempts = JSON.parse(sessionStorage.getItem(KEY) || "[]").filter((t: number) => now - t < 120000); } catch { /* ignore */ }
      if (attempts.length >= 2) {
        setMessage("SHOPLINE keeps asking to authorize Ocesha. Click Continue to authorize again, or re-open Ocesha from SHOPLINE admin → Apps.");
        setPhase("authorize_click");
        return;
      }
      try { sessionStorage.setItem(KEY, JSON.stringify([...attempts, now])); } catch { /* ignore */ }
      setPhase("authorizing");
      startShoplineOauth({
        appKey: params.get("app_key") || "",
        scope: params.get("scope") || "",
        redirectUri: params.get("redirect_uri") || "",
        authUrl,
      }).then((ok: boolean) => { if (!ok) setPhase("authorize_click"); });
      // If nothing happened after a few seconds, offer a manual button (user gesture).
      const t = setTimeout(() => setPhase((p) => (p === "authorizing" ? "authorize_click" : p)), 6000);
      return () => clearTimeout(t);
    }

    const code = params.get("code");
    if (!code) {
      setPhase("ended");
      return;
    }
    (async () => {
      try {
        const session = await exchangeSessionCode(code);
        startEmbeddedSession(session);
        try { sessionStorage.removeItem("ocesha_oauth_attempts"); } catch { /* ignore */ }
        // No onboarding for SHOPLINE merchants:
        //   no plan yet  → pricing (Stripe opens in a popup, then back to suggestions)
        //   has a plan   → social post suggestions built from their store's products
        localStorage.setItem("activeItem", "authority");
        if (!session.user.subscription_level && session.user.role === "user") {
          navigate("/pricing", { replace: true, state: { from_page: "pricing" } });
        } else {
          navigate("/codegenie/generated-blogs?activeItem=authority", { replace: true });
        }
      } catch (e: any) {
        setMessage(e?.response?.data?.detail || "Your session link expired.");
        setPhase(e?.response?.status === 410 ? "ended" : "error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const card = (children: React.ReactNode) => (
    <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2,
      background: "linear-gradient(180deg, #0B1220 0%, #06080F 100%)", color: "#fff",
    }}>
      <Box sx={{
        width: "100%", maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 3, textAlign: "center",
        background: "#0F1117", border: "1px solid rgba(255,255,255,0.08)",
      }}>
        <Box component="img" src={OceshaLogo} alt="Ocesha"
             sx={{ display: "block", height: 40, mx: "auto", mb: 2.5, borderRadius: 1 }} />
        {children}
      </Box>
    </Box>
  );

  if (phase === "loading" || phase === "authorizing") {
    return card(
      <>
        <CircularProgress size={28} sx={{ color: "#4BA5EB" }} />
        <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.65)", fontSize: 14 }}>
          {phase === "authorizing" ? "Connecting Ocesha to your store…" : "Opening Ocesha…"}
        </Typography>
      </>
    );
  }

  if (phase === "nested") {
    return card(
      <>
        <StorefrontIcon sx={{ fontSize: 36, color: "#4BA5EB", mb: 1 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>Ocesha is installed</Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 14, mb: 3 }}>
          Click below to open Ocesha in your SHOPLINE admin.
        </Typography>
        <Button fullWidth variant="contained"
          onClick={() => { if (!breakOut(adminUrl)) window.open(adminUrl, "_top"); }}
          sx={{ textTransform: "none", fontWeight: 600, py: 1.2, background: "#4BA5EB", "&:hover": { background: "#3b94d9" } }}>
          Open Ocesha
        </Button>
      </>
    );
  }

  if (phase === "authorize_click") {
    return card(
      <>
        <StorefrontIcon sx={{ fontSize: 36, color: "#4BA5EB", mb: 1 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>Allow Ocesha to access your store</Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 14, mb: 3 }}>
          {message || "Ocesha reads your products to create social posts and blogs for them."}
        </Typography>
        <Button fullWidth variant="contained"
          onClick={() => { try { (window.top as Window).location.href = authUrl; } catch { window.open(authUrl, "_top"); } }}
          sx={{ textTransform: "none", fontWeight: 600, py: 1.2, background: "#4BA5EB", "&:hover": { background: "#3b94d9" } }}>
          Continue
        </Button>
      </>
    );
  }

  const title = phase === "outside" ? "Open Ocesha from SHOPLINE"
    : phase === "ended" ? "Your Ocesha session has ended"
    : "We couldn't open Ocesha";
  const body = phase === "outside"
    ? "Ocesha runs inside your SHOPLINE admin. Go to SHOPLINE admin → Apps → Ocesha."
    : phase === "ended"
    ? "Re-open Ocesha from SHOPLINE admin → Apps to continue."
    : `${message} Re-open Ocesha from SHOPLINE admin → Apps to try again.`;

  return card(
    <>
      {phase === "error" && <ErrorOutlineIcon sx={{ display: "block", mx: "auto", fontSize: 34, color: "#F87171", mb: 1 }} />}
      <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>{title}</Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 14 }}>{body}</Typography>
    </>
  );
};

export default ShoplineEmbeddedApp;
